/**
 * HLOS-28 — HKDF-SHA256 wrapper (espelha `hlos/vault/crypto.py::hkdf_sha256`).
 *
 * Implementação via WebCrypto (`crypto.subtle.deriveBits` com HKDF).
 *
 * NOTA sobre paridade byte-a-byte com Python:
 *   - Python: HKDF-SHA256 (RFC 5869) com salt fixo `HKDF_SALT_V1 = b"hlos-vault-hkdf-v1"`.
 *   - WebCrypto HKDF: aceita salt como parâmetro `salt` (opcional, gerado random
 *     se omitido). Passamos nosso salt fixo para obter mesmo output.
 *   - Output: 32 bytes (256 bits) wrap key, espelha Python `derive_wrap_key()`.
 *
 * Por que HKDF (não KDF direto):
 *   - Domain separation entre KEK e AES-GCM wrap key (NIST SP 800-108)
 *   - Mesmo IKM pode gerar múltiplas chaves para contextos diferentes
 *
 * Referência: `docs/architecture/IDENTITY_AND_KEY_MANAGEMENT.md` §3.2.
 */

import {
  MNEMONIC_INFO,
  PASSPHRASE_INFO,
  WRAP_INFO,
} from "./constants";

// ─────────────────────────────────────────────────────────────────────────────
// Constantes (espelham Python — KEEP IN SYNC)
// ─────────────────────────────────────────────────────────────────────────────

/** Salt fixo HKDF-SHA256 (deve coincidir com `crypto.HKDF_SALT_V1` Python). */
export const HKDF_SALT_V1 = new TextEncoder().encode("hlos-vault-hkdf-v1");

/** Domain separation: passphrase → KEK. Espelha `crypto.PASSPHRASE_INFO`. */
export { PASSPHRASE_INFO };

/** Domain separation: mnemonic → KEK. Espelha `crypto.MNEMONIC_INFO`. */
export { MNEMONIC_INFO };

/** Domain separation: KEK → wrap key (AES-GCM). Espelha `crypto.WRAP_INFO`. */
export { WRAP_INFO };

/** Tamanho padrão do output (256 bits = 32 bytes = AES-256 key size). */
export const DEFAULT_HKDF_LENGTH_BYTES = 32;

// ─────────────────────────────────────────────────────────────────────────────
// Erros
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Erro thrown quando HKDF é invocado com length inválido (CL-08).
 */
export class InvalidHkdfLengthError extends Error {
  constructor(actualLength: number) {
    super(
      `HKDF length must be > 0 and ≤ 255 * 32 bytes (8160), got ${actualLength}`,
    );
    this.name = "InvalidHkdfLengthError";
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// HKDF-SHA256
// ─────────────────────────────────────────────────────────────────────────────

/**
 * HKDF-SHA256 (RFC 5869). Espelha `crypto.hkdf_sha256()` (Python).
 *
 * Extrai + expande para gerar `length` bytes de key material a partir de
 * `ikm`, usando `info` como domain separation e `salt` como HKDF salt.
 *
 * Usa salt fixo `HKDF_SALT_V1` (consistente com Python) — NUNCA random
 * salt (que daria output diferente a cada chamada).
 *
 * @param ikm - Input Keying Material (32+ bytes para segurança HKDF).
 * @param info - Domain separation bytes (ex: `WRAP_INFO`).
 * @param length - Output length em bytes (default 32 = AES-256).
 *
 * @throws InvalidHkdfLengthError se length ≤ 0 ou > 8160.
 */
export async function hkdfSha256(
  ikm: Uint8Array,
  info: Uint8Array,
  length: number = DEFAULT_HKDF_LENGTH_BYTES,
): Promise<Uint8Array> {
  if (length <= 0 || length > 255 * 32) {
    throw new InvalidHkdfLengthError(length);
  }

  const ikmKey = await crypto.subtle.importKey(
    "raw",
    ikm as BufferSource,
    "HKDF",
    false,
    ["deriveBits"],
  );

  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: HKDF_SALT_V1 as BufferSource,
      info: info as BufferSource,
    },
    ikmKey,
    length * 8,
  );

  return new Uint8Array(derivedBits);
}

/**
 * Deriva wrap key (AES-GCM) a partir de KEK. Espelha `crypto.derive_wrap_key`.
 *
 * Convenience wrapper sobre `hkdfSha256(kek, WRAP_INFO, 32)`.
 */
export function deriveWrapKey(kek: Uint8Array): Promise<Uint8Array> {
  return hkdfSha256(kek, WRAP_INFO, 32);
}

